import React, { useState, useEffect } from "react";
import { apiFetch } from "../api";
import { useLoading } from "../contexts/LoadingContext";
import { SendIcon, CancelIcon, ChevronLeftIcon } from "./icons";

const CreateReferralModal = ({
  isOpen,
  onClose,
  onCreateReferral,
  preselectedClient,
}) => {
  const [step, setStep] = useState(1); // 1: Select client, 2: Select profile, 3: Configure referral
  const [clients, setClients] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [referralData, setReferralData] = useState({
    clientId: "",
    profileId: "",
    bureauNotes: "",
    followUpDate: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { setLoading: setGlobalLoading, setGlobalLoadingMessage } = useLoading();

  // Reset modal state when opened/closed.
  // If a `preselectedClient` is passed in (e.g. opening from the
  // per-client matches page), skip step 1 entirely and seed both
  // `selectedClient` and `referralData.clientId`. We still call
  // fetchClients() so the user can change clients later if they
  // really want to — opening the modal pre-scoped is a default, not
  // a hard lock.
  useEffect(() => {
    if (isOpen) {
      const preSel = preselectedClient || null;
      setStep(preSel ? 2 : 1);
      setSelectedClient(preSel);
      setSelectedProfile(null);
      setReferralData({
        clientId: preSel?._id || "",
        profileId: "",
        bureauNotes: "",
        followUpDate: "",
      });
      setError(null);
      setSearchTerm("");
      fetchClients();
    }
  }, [isOpen, preselectedClient]);

  const fetchClients = async () => {
    try {
      const data = await apiFetch("/clients");
      setClients(data.data || []);
    } catch (err) {
      console.error("Failed to fetch clients:", err);
    }
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setReferralData(prev => ({ ...prev, clientId: client._id }));
    setStep(2);
  };

  const handleProfileSearch = async () => {
    if (!searchTerm.trim()) return;

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        search: searchTerm,
        status: "active",
        excludeUserId: selectedClient?.user?._id,
        clientId: selectedClient?._id,
      });

      const data = await apiFetch(`/profiles/search-profiles?${params}`);
      const profiles = Array.isArray(data) ? data : data.data || [];

      const filteredProfiles = profiles.filter(
        (profile) => profile._id !== selectedClient?.user?._id
      );

      setProfiles(filteredProfiles);
    } catch (err) {
      console.error("Search failed:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSelect = (profile) => {
    setSelectedProfile(profile);
    setReferralData(prev => ({ ...prev, profileId: profile._id }));
    setStep(3);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedClient || !selectedProfile) {
      setError("Please select both client and profile");
      return;
    }

    try {
      setGlobalLoadingMessage("Creating referral...");
      setGlobalLoading(true);
      
      await onCreateReferral(referralData);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  };

  const handleInputChange = (field, value) => {
    setReferralData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--color-surface)] rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* HEADER — always visible */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-[var(--color-border)] flex-shrink-0">
          <h2 className="text-xl font-semibold text-[var(--color-text-strong)]">
            Create New Referral
          </h2>
          <button
            onClick={onClose}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
          >
            ✕
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col flex-1 overflow-hidden"
        >
          {/* BODY — scrolls */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Step Indicator */}
          <div className="flex items-center mb-6">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 1 ? 'bg-[var(--color-primary)] text-[var(--color-on-primary)]' : 'bg-[var(--color-border)] text-[var(--color-text-muted)]'}`}>
              1
            </div>
            <div className={`flex-1 h-1 mx-2 ${step >= 2 ? 'bg-[var(--color-primary)]' : 'bg-[var(--color-border)]'}`}></div>
            <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 2 ? 'bg-[var(--color-primary)] text-[var(--color-on-primary)]' : 'bg-[var(--color-border)] text-[var(--color-text-muted)]'}`}>
              2
            </div>
            <div className={`flex-1 h-1 mx-2 ${step >= 3 ? 'bg-[var(--color-primary)]' : 'bg-[var(--color-border)]'}`}></div>
            <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 3 ? 'bg-[var(--color-primary)] text-[var(--color-on-primary)]' : 'bg-[var(--color-border)] text-[var(--color-text-muted)]'}`}>
              3
            </div>
          </div>

          {/* Step 1: Select Client */}
          {step === 1 && (
            <div>
              <h3 className="text-lg font-medium text-[var(--color-text-strong)] mb-4">
                Select Client
              </h3>

              {clients.length === 0 ? (
                <div className="text-center py-8 text-[var(--color-text-muted)]">
                  No clients available. Add clients first.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                  {clients.map((client) => (
                    <div
                      key={client._id}
                      onClick={() => handleClientSelect(client)}
                      className="p-4 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-hover)] cursor-pointer transition-colors"
                    >
                      <div className="font-medium text-[var(--color-text-strong)]">
                        {client.user?.fullName || "Unknown"}
                      </div>
                      <div className="text-sm text-[var(--color-text-muted)]">
                        ID: {client.user?.referenceId}
                      </div>
                      <div className="text-sm text-[var(--color-text-muted)]">
                        {client.user?.contactNumber} • {client.user?.city}
                      </div>
                      <div className="text-xs text-[var(--color-text-muted)] mt-2">
                        Active: {client.activeReferrals || 0} • Success: {client.successfulMatches || 0}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Select Profile */}
          {step === 2 && (
            <div>
              <h3 className="text-lg font-medium text-[var(--color-text-strong)] mb-4">
                Select Profile to Refer
              </h3>

              {/* Selected Client Info */}
              <div className="bg-[var(--color-surface-muted)] p-3 rounded-lg mb-4">
                <div className="text-sm text-[var(--color-text-muted)]">Selected Client:</div>
                <div className="font-medium text-[var(--color-text-strong)]">
                  {selectedClient?.user?.fullName}
                </div>
              </div>

              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  placeholder="Search profiles by name, reference ID, or criteria..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleProfileSearch();
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-background)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                />
                <button
                  type="button"
                  onClick={handleProfileSearch}
                  disabled={loading}
                  className="px-4 py-2 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] disabled:opacity-50 transition-colors"
                >
                  {loading ? "Searching..." : "Search"}
                </button>
              </div>

              {error && (
                <div className="text-red-500 text-sm mb-4">{error}</div>
              )}

              {profiles.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                  {profiles.map((profile) => (
                    <div
                      key={profile._id}
                      onClick={() => handleProfileSelect(profile)}
                      className="p-4 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-hover)] cursor-pointer transition-colors"
                    >
                      <div className="font-medium text-[var(--color-text-strong)]">
                        {profile.fullName}
                      </div>
                      <div className="text-sm text-[var(--color-text-muted)]">
                        ID: {profile.referenceId} • Age: {profile.age} • {profile.city}
                      </div>
                      <div className="text-sm text-[var(--color-text-muted)]">
                        {profile.profession} • {profile.maritalStatus}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {profiles.length === 0 && searchTerm && !loading && (
                <div className="text-center py-8 text-[var(--color-text-muted)]">
                  No profiles found. Try different search terms.
                </div>
              )}
            </div>
          )}

          {/* Step 3: Configure Referral */}
          {step === 3 && selectedClient && selectedProfile && (
            <div>
              <h3 className="text-lg font-medium text-[var(--color-text-strong)] mb-4">
                Configure Referral
              </h3>

              {/* Selected Client and Profile Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-[var(--color-surface-muted)] p-3 rounded-lg">
                  <div className="text-sm text-[var(--color-text-muted)]">Client:</div>
                  <div className="font-medium text-[var(--color-text-strong)]">
                    {selectedClient.user?.fullName}
                  </div>
                  <div className="text-sm text-[var(--color-text-muted)]">
                    {selectedClient.user?.referenceId}
                  </div>
                </div>
                <div className="bg-[var(--color-surface-muted)] p-3 rounded-lg">
                  <div className="text-sm text-[var(--color-text-muted)]">Profile:</div>
                  <div className="font-medium text-[var(--color-text-strong)]">
                    {selectedProfile.fullName}
                  </div>
                  <div className="text-sm text-[var(--color-text-muted)]">
                    {selectedProfile.referenceId}
                  </div>
                </div>
              </div>

              {error && (
                <div className="text-red-500 text-sm mb-4">{error}</div>
              )}

              <div className="space-y-4">
                {/* Bureau Notes */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-1">
                    Bureau Notes (Optional)
                  </label>
                  <textarea
                    value={referralData.bureauNotes}
                    onChange={(e) => handleInputChange('bureauNotes', e.target.value)}
                    placeholder="Internal notes about this referral..."
                    rows={3}
                    className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-background)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  />
                </div>

                {/* Follow-up Date */}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-strong)] mb-1">
                    Follow-up Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={referralData.followUpDate}
                    onChange={(e) => handleInputChange('followUpDate', e.target.value)}
                    className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-background)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  />
                </div>
              </div>

            </div>
          )}
          </div>

          {/* FOOTER — always visible. Buttons swap by step. */}
          <div className="flex justify-between gap-3 px-6 py-4 border-t border-[var(--color-border)] bg-[var(--color-surface)] flex-shrink-0">
            <button
              type="button"
              onClick={step === 1 ? onClose : () => setStep(step - 1)}
              className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] px-4 py-2 text-sm font-semibold transition-colors"
            >
              {step === 1 ? <CancelIcon className="w-4 h-4" /> : <ChevronLeftIcon className="w-4 h-4" />}
              {step === 1 ? "Cancel" : "Back"}
            </button>
            <button
              type="submit"
              disabled={step !== 3}
              className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] disabled:opacity-50 px-4 py-2 text-sm font-semibold shadow-sm transition-colors"
            >
              <SendIcon className="w-4 h-4" />
              Create Referral
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateReferralModal;